import { SHARED_COLORS, type SharedColor } from "@/lib/canvas/shared-shape";

/**
 * Six-swatch color picker shared by both canvas surfaces.
 *
 * Each swatch carries `data-canvas-color="<hex>"` so Playwright can
 * target it without depending on DOM order. The active swatch gets a
 * ring + raised z-index for keyboard focus visibility.
 */

export type ColorPaletteProps = {
  value: SharedColor;
  onChange: (color: SharedColor) => void;
  /** Optional label rendered above the swatches. Defaults to "Color". */
  label?: string;
};

export function ColorPalette(props: ColorPaletteProps) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-display text-ink-muted uppercase tracking-wider">
        {props.label ?? "Color"}
      </span>
      <div
        className="flex flex-wrap justify-center gap-2 px-1"
        role="radiogroup"
        aria-label="Color"
      >
        {SHARED_COLORS.map((c) => {
          const active = c === props.value;
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`Color ${c}`}
              data-canvas-color={c}
              onClick={() => props.onChange(c)}
              className={`relative w-7 h-7 rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                active
                  ? "border-ink ring-2 ring-ink"
                  : "border-rule hover:border-ink-muted"
              }`}
              style={{ backgroundColor: c }}
            />
          );
        })}
      </div>
    </div>
  );
}
